import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Snackbar } from '@mui/material';
import { Bounce, toast } from 'react-toastify';
import classes from "./registrationForm.module.css";
import { 
  GENDER_OPTIONS, 
  ROLE_OPTIONS, 
  MUSIC_CERTIFICATION_OPTIONS, 
  ENGLISH_PROFICIENCY_OPTIONS, 
  TUTOR_QUALIFICATION_OPTIONS, 
  YEAR_OF_EXP_OPTIONS, 
  HOME_SETUP_OPTIONS 
} from '../../../constants';
import CheckIcon from '@mui/icons-material/Check';
import CircularProgress from "@mui/material/CircularProgress";
import VerifyWhatsAppNumModal from "./VerifyWhatsAppNumModal";
import Header from "./dashboard-navbar";
import { Button } from "@mui/material";

// Memoized field component to prevent unnecessary re-renders
const FormField = React.memo(({ 
  label, 
  type = "text", 
  value, 
  onChange, 
  error, 
  options = null, 
  placeholder = "",
  description = "",
  ...props 
}) => (
  <div className="md:mb-4 mb-2">
    <label className="block text-sm font-Inter mb-2 font-medium text-gray-700">
      {label}
    </label>
    {description && (
      <p className="text-xs text-gray-500 mb-2">{description}</p>
    )}
    {options ? (
      <select
        value={value}
        onChange={onChange}
        className="w-full font-Inter border-gray-300 rounded-md border-[1px] p-2"
        {...props}
      >
        <option value="">Select</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    ) : (
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full font-Inter border-gray-300 border-[1px] rounded-md p-2"
        {...props}
      />
    )}
    {error && (
      <span className="text-red-600 text-sm font-Inter">{error}</span>
    )}
  </div>
));

// Country codes data
const COUNTRY_CODES = [
  { code: '+1', country: 'US/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
  { code: '+66', country: 'Thailand', flag: '🇹🇭' },
  { code: '+84', country: 'Vietnam', flag: '🇻🇳' },
  { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭' },
  { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
  { code: '+880', country: 'Bangladesh', flag: '🇧🇩' },
  { code: '+94', country: 'Sri Lanka', flag: '🇱🇰' },
  { code: '+977', country: 'Nepal', flag: '🇳🇵' },
  { code: '+20', country: 'Egypt', flag: '🇪🇬' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  { code: '+234', country: 'Nigeria', flag: '🇳🇬' },
  { code: '+254', country: 'Kenya', flag: '🇰🇪' }
];

// Custom hook for form state management
const useFormState = (initialState) => {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const resetForm = useCallback(() => {
    setFormData(initialState);
    setErrors({});
  }, [initialState]);

  return { formData, errors, setErrors, updateField, resetForm };
};

// Validation schema
const validationSchema = {
  userName: (value) => !value?.trim() ? 'Required' : null,
  phoneNumber: (value) => !value?.trim() ? 'Required' : null,
  gender: (value) => !value?.trim() ? 'Required' : null,
  englishProficiency: (value) => !value?.trim() ? 'Required' : null,
  yearOfExperience: (value) => !value ? 'Required' : null,
  musicCertification: (value) => !value ? 'Required' : null,
  homesetup: (value) => !value ? 'Required' : null,
  highestLevelOfQualification: (value) => !value ? 'Required' : null,
};

const ApplicationForm = () => {
  // Initial form state
  const initialFormState = {
    userName: '',
    role: '',
    phoneNumber: '',
    countryCode: '+91', // Default to India
    gender: '',
    englishProficiency: '',
    yearOfExperience: '',
    musicCertification: '',
    homesetup: '',
    whatsapp_no: '',
    date: '',
    highestLevelOfQualification: '',
    introduction: ''
  };

  const { formData, errors, setErrors, updateField, resetForm } = useFormState(initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [whatsappState, setWhatsappState] = useState({
    waError: false,
    waErrorMsg: "",
    isWhatsappVerified: false,
    waVerifyProgressBar: false,
    verifiedNum: null,
    whatsappDialogOpen: false
  });

  const history = useNavigate();

  // Memoized validation function
  const validateForm = useCallback(() => {
    const validationErrors = {};
    
    Object.keys(validationSchema).forEach(field => {
      const error = validationSchema[field](formData[field]);
      if (error) {
        validationErrors[field] = error;
      }
    });

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  }, [formData, setErrors]);

  // Debounced phone number validation
  const handlePhoneNumber = useCallback((value) => {
    setWhatsappState(prev => ({
      ...prev,
      waError: false,
      waErrorMsg: "",
      isWhatsappVerified: false
    }));

    const re = /^[0-9\b]+$/;
    if (value === "" || re.test(value)) {
      updateField('phoneNumber', value);
    }
  }, [updateField]);

  // Handle country code change
  const handleCountryCodeChange = useCallback((code) => {
    updateField('countryCode', code);
    setWhatsappState(prev => ({
      ...prev,
      waError: false,
      waErrorMsg: "",
      isWhatsappVerified: false
    }));
  }, [updateField]);

  // Function to format phone number for API (remove + and concat)
  const formatPhoneForAPI = useCallback((countryCode, phoneNumber) => {
    const cleanCountryCode = countryCode.replace('+', '');
    const cleanPhoneNumber = phoneNumber.replace(/\D/g, ''); // Remove non-digits
    return `${cleanCountryCode}${cleanPhoneNumber}`;
  }, []);

  // Memoized API endpoints
  const API_ENDPOINTS = useMemo(() => ({
    CHECK_WA: "https://testapis4.el.r.appspot.com/api/tutorWeb/checkWaInDb",
    SUBMIT_FORM: "https://testapis4.el.r.appspot.com/api/tutorWeb/setMusicTutorDetailsByForm",
    UPDATE_MAIN: "https://central-device-apis.el.r.appspot.com/api/tutorWeb/updateTutorInfoFromMusic"
  }), []);

  // Optimized WhatsApp verification
  const handleWhatsappDialogOpen = useCallback(async () => {
    try {
      if (!formData.userName) {
        alert("Please enter your name");
        return;
      }
      if (!formData.phoneNumber) {
        alert("Please enter Whatsapp number");
        return;
      }

      setWhatsappState(prev => ({ ...prev, waVerifyProgressBar: true }));

      // Format phone number for API (remove + and concat)
      const formattedPhoneNumber = formatPhoneForAPI(formData.countryCode, formData.phoneNumber);

      const checkInMainList = await axios.post(API_ENDPOINTS.CHECK_WA, { 
        wa_id: formattedPhoneNumber 
      });

      if (checkInMainList?.data?.success) {
        setWhatsappState(prev => ({
          ...prev,
          waError: true,
          waVerifyProgressBar: false,
          waErrorMsg: "Whatsapp number already exist!"
        }));
      } else {
        setWhatsappState(prev => ({
          ...prev,
          verifiedNum: formattedPhoneNumber,
          whatsappDialogOpen: true,
          waVerifyProgressBar: false
        }));
      }
    } catch (err) {
      console.error('WhatsApp verification error:', err);
      setWhatsappState(prev => ({ ...prev, waVerifyProgressBar: false }));
    }
  }, [formData.userName, formData.phoneNumber, formData.countryCode, API_ENDPOINTS.CHECK_WA, formatPhoneForAPI]);

  // Optimized form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (isLoading) return; // Prevent double submission
    
    setIsLoading(true);
    
    try {
      const isValid = validateForm();
      
      if (!isValid) {
        setIsLoading(false);
        return;
      }

      if (!whatsappState.isWhatsappVerified) {
        alert('Please verify your WhatsApp number!');
        setIsLoading(false);
        return;
      }

      const tutorData = JSON.parse(localStorage?.getItem("tutor") || '{}');
      const tutorId = tutorData?.saveTutor?.tutor_id;

      if (!tutorId) {
        throw new Error('Tutor ID not found');
      }

      // Format phone number for API (remove + and concat)
      const formattedPhoneNumber = formatPhoneForAPI(formData.countryCode, formData.phoneNumber);

      const payload = {
        name: formData.userName,
        gender: formData.gender,
        homesetup: formData.homesetup,
        englishProficiency: formData.englishProficiency,
        musicCertification: formData.musicCertification,
        highest_degree: formData.highestLevelOfQualification,
        yearOfExperience: formData.yearOfExperience,
        wa_id: formattedPhoneNumber, // Send formatted number without +
        tutStatus: "intrvfm",
        introduction: formData.introduction
      };

      // Use Promise.all for concurrent API calls
      const [response, responseInMain] = await Promise.all([
        axios.post(`${API_ENDPOINTS.SUBMIT_FORM}/${tutorId}`, payload),
        axios.post(`${API_ENDPOINTS.UPDATE_MAIN}/${tutorId}`, payload)
      ]);

      if (response?.status === 200 && responseInMain?.data) {
        toast('Your Order details has been sent.', {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          transition: Bounce,
        });
        
        history("/tutor_exam/interviews",{replace:true});
        resetForm();
      }
    } catch (err) {
      console.error('Form submission error:', err);
      toast.error('Something went wrong. Please try again later.', {
        position: 'top-center',
        autoClose: 5000,
        theme: 'light',
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    formData, 
    isLoading, 
    validateForm, 
    whatsappState.isWhatsappVerified, 
    API_ENDPOINTS, 
    history, 
    resetForm,
    formatPhoneForAPI
  ]);

  const handleWhatsappDialogClose = useCallback(() => {
    setWhatsappState(prev => ({ ...prev, whatsappDialogOpen: false }));
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Memoized form fields configuration
  const formFields = useMemo(() => [
    {
      label: "Full Name",
      value: formData.userName,
      onChange: (e) => updateField('userName', e.target.value),
      error: errors.userName,
      key: 'userName'
    },
    {
      label: "Gender",
      value: formData.gender,
      onChange: (e) => updateField('gender', e.target.value),
      error: errors.gender,
      options: GENDER_OPTIONS,
      key: 'gender'
    },
    {
      label: "Highest Level Of Qualification",
      value: formData.highestLevelOfQualification,
      onChange: (e) => updateField('highestLevelOfQualification', e.target.value),
      error: errors.highestLevelOfQualification,
      options: TUTOR_QUALIFICATION_OPTIONS,
      key: 'qualification'
    },
    {
      label: "Rate English Proficiency",
      value: formData.englishProficiency,
      onChange: (e) => updateField('englishProficiency', e.target.value),
      error: errors.englishProficiency,
      options: ENGLISH_PROFICIENCY_OPTIONS,
      key: 'english'
    },
    {
      label: "Years Of Experience",
      value: formData.yearOfExperience,
      onChange: (e) => updateField('yearOfExperience', e.target.value),
      error: errors.yearOfExperience,
      options: YEAR_OF_EXP_OPTIONS,
      key: 'experience'
    },
    {
      label: "Music Certification",
      value: formData.musicCertification,
      onChange: (e) => updateField('musicCertification', e.target.value),
      error: errors.musicCertification,
      options: MUSIC_CERTIFICATION_OPTIONS,
      key: 'certification'
    },
    {
      label: "Home Setup",
      value: formData.homesetup,
      onChange: (e) => updateField('homesetup', e.target.value),
      error: errors.homesetup,
      options: HOME_SETUP_OPTIONS,
      description: "Do you have suitable home setup for online teaching? (Mic/Headset, webcam, Laptop & Wifi)",
      key: 'homesetup'
    }
  ], [formData, errors, updateField]);

  return (
    <>
      <span style={{ width: "100%" }}>
        <Header />
      </span>
      <motion.section 
        exit={{ opacity: 0 }} 
        style={{
          backgroundImage: 'url("/backgroundTuro.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="mb-10 bg-no-repeat w-full">
          <div className="flex justify-center items-center h-full">
            <div className="md:mt-44 mt-64">
              <h3 className="text-3xl lg:text-[44px] font-Inter font-bold text-orange-500">
                Job Application
              </h3>
            </div>
          </div>
        </div>
        
        <div className='-mt-32 rounded-md flex w-full justify-center items-center pb-16'>
          <form
            onSubmit={handleSubmit}
            className="bg-white w-[750px] px-4 mx-2 md:mx-0 md:px-10 py-8 md:py-14 rounded-md shadow-[0_35px_60px_-15px_rgba(0,0,0,0.2)]"
          >
            <h2 className="md:text-[40px] text-2xl font-Inter font-bold md:mb-6 mb-4 text-center">
              Registration Form
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {formFields.map((field) => (
                <FormField key={field.key} {...field} />
              ))}
              
              {/* WhatsApp Number Field */}
              <div className="md:mb-4 mb-2">
                <label className="block text-sm font-Inter mb-2 font-medium text-gray-700">
                  WhatsApp Number
                </label>
                {whatsappState.waError ? (
                  <p className="text-xs text-red-500 mb-2">
                    {whatsappState.waErrorMsg}
                  </p>
                ) : (
                  <p className="text-xs text-green-500 mb-2">
                    *Select country code and enter your WhatsApp number
                  </p>
                )}
                <div className="flex">
                  {/* Country Code Selector */}
                  <select
                    value={formData.countryCode}
                    onChange={(e) => handleCountryCodeChange(e.target.value)}
                    className="flex-shrink-0 w-32 font-Inter border-gray-300 rounded-l-md border-[1px] border-r-0 p-2 bg-gray-50"
                  >
                    {COUNTRY_CODES.map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.flag} {item.code}
                      </option>
                    ))}
                  </select>
                  {/* Phone Number Input */}
                  <input
                    type="tel"
                    placeholder="Enter phone number"
                    value={formData.phoneNumber}
                    onChange={(e) => handlePhoneNumber(e.target.value)}
                    className="flex-grow font-Inter border-gray-300 rounded-r-md border-[1px] border-l-0 p-2"
                  />
                </div>
                {/* Display formatted number */}
                {formData.phoneNumber && (
                  <p className="text-xs text-gray-600 mt-1">
                    Full number: {formData.countryCode} {formData.phoneNumber}
                  </p>
                )}
                {errors.phoneNumber && (
                  <span className="text-red-600 text-sm font-Inter">
                    {errors.phoneNumber}
                  </span>
                )}
                {whatsappState.isWhatsappVerified ? (
                  <Button
                    variant="outlined"
                    color="success"
                    sx={{ alignSelf: "flex-start" }}
                    style={{ marginTop: "10px" }}
                    startIcon={<CheckIcon />}
                  >
                    Verified
                  </Button>
                ) : (
                  <Button 
                    variant="contained" 
                    sx={{ alignSelf: "flex-start" }} 
                    style={{ marginTop: "10px" }} 
                    onClick={handleWhatsappDialogOpen}
                    disabled={whatsappState.waVerifyProgressBar}
                  >
                    {whatsappState.waVerifyProgressBar && (
                      <CircularProgress
                        style={{
                          width: "22px",
                          height: "22px",
                          marginRight: "10px",
                        }}
                        color="inherit"
                      />
                    )}
                    Verify
                  </Button>
                )}
              </div>
            </div>
            
            {/* Introduction Field */}
            <div className="md:mb-4 mb-2 w-full">
              <label className="block text-sm font-Inter mb-2 font-medium text-gray-700">
                Introduction
              </label>
              <textarea
                rows={4}
                placeholder="Write your brief introduction..."
                value={formData.introduction}
                onChange={(e) => updateField('introduction', e.target.value)}
                className="w-full font-Inter border-gray-300 rounded-md border-[1px] p-2"
              />
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              className="inline-block w-full bg-[#663399] rounded-lg px-5 py-3 font-medium font-Poppins hover:bg-lightGreen text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '8px' }} />
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </button>
          </form>
        </div>
      </motion.section>
      
      {whatsappState.whatsappDialogOpen && (
        <VerifyWhatsAppNumModal 
          open={whatsappState.whatsappDialogOpen}
          handleClose={handleWhatsappDialogClose} 
          setIsWhatsappVerified={(verified) => 
            setWhatsappState(prev => ({ ...prev, isWhatsappVerified: verified }))
          }
          waNum={whatsappState.verifiedNum} 
          name={formData.userName} 
        />
      )}
    </>
  );
};

export default ApplicationForm;